import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import { AutoTooltipContent } from 'components/Common/AutoTooltip'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { StandaloneFlightMenu } from 'components/Flights/FlightsSearch/SearchWidget/SearchWidget'
import { mediaQueryUp } from 'components/utils/breakpoint'

import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import LocationInput from './LocationInput'
import ExchangeButton from './ExchangeButton'

const Root = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${props => props.theme.borderRadius.small};
  position: relative;
  width: 100%;
  background: ${props => props.theme.palette.neutral.eight};

  ${mediaQueryUp.lg} {
    position: static;
  }

  &.error {
    background: ${props => props.theme.palette.messaging.critical.light};

    > button {
      border: solid ${props => props.theme.palette.messaging.critical.main};
      border-width: 1px 0;
    }
  }
`

const DepartureAirportInput = styled(LocationInput)`
  border-top-left-radius: ${props => props.theme.borderRadius.small};
  border-bottom-left-radius: ${props => props.theme.borderRadius.small};
  flex: 1;
`

const ArrivalAirportInput = styled(LocationInput)`
  border-top-right-radius: ${props => props.theme.borderRadius.small};
  border-bottom-right-radius: ${props => props.theme.borderRadius.small};
`

interface Props {
  className?: string
}

function LocationInputDesktop(props: Props) {
  const { className } = props
  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const {
    departureAirport,
    arrivalAirport,
    activeMenu,
    showDepartureError,
    showDesinationError,
    showSameAirportError,
  } = state

  const getErrorMessage = () => {
    if (showDepartureError && showDesinationError) {
      return 'Please enter departure and destination'
    } else if (showDesinationError) {
      return 'Please enter a destination'
    } else if (showSameAirportError) {
      return 'Origin and Destination cannot be the same'
    } else {
      return 'Please enter a departure'
    }
  }

  const onSwapAirports = useCallback(() => {
    const departure = departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, airport: arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, airport: departure })
  }, [departureAirport, dispatch, arrivalAirport])

  const setActiveMenu = useCallback((activeMenu: StandaloneFlightMenu) => {
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu })
  }, [dispatch])

  return (
    <Root className={cn(className, { error: (showDepartureError && showDesinationError) || showSameAirportError })}>
      <DepartureAirportInput
        isDeparture
        title="From"
        airport={departureAirport}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />
      <ExchangeButton onClick={onSwapAirports} />
      <ArrivalAirportInput
        isDeparture={false}
        title="To"
        airport={arrivalAirport}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
      />
      <AutoTooltipContent
        open={showDepartureError || showDesinationError || showSameAirportError}
        position="above-left"

        alignment="left"
        type="error"
      >
        {getErrorMessage()}
      </AutoTooltipContent>
    </Root>
  )
}

export default LocationInputDesktop
